//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import styled from '@emotion/styled';
import { FilledInput, FormControl, Grow, IconButton, Tooltip } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';
import FilterChip from '@/components/_common/Filters/FilterChip';

type SearchBarProps = {
  role: ReturnType<typeof createSelector>;
};

function SearchBar({ role }: SearchBarProps) {
  const hasPermissions = role ? useAppSelector(role) : true;
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDataTableFilters);
  const searchTerm = filters.search;
  const [textFilter, setTextFilter] = useState('');
  const [view, setView] = useState<'no-filter' | 'edit' | 'filter'>('no-filter');
  const inputRef = useRef<HTMLInputElement>(null);
  const stashedTextFilter = useRef('');
  const isV2 = useContext(ConfigContext)?.filtersVersion === 2;

  const onKeyEvent = useCallback(
    (event: KeyboardEvent): void => {
      if (document.activeElement?.tagName?.toLocaleLowerCase() === 'body' && hasPermissions && event.key === '/' && view !== 'edit') {
        stashedTextFilter.current = searchTerm;
        setTextFilter(searchTerm);
        setView('edit');
        event.stopPropagation();
        event.preventDefault();
      }
    },
    [view, hasPermissions, searchTerm]
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyEvent);

    return () => {
      document.removeEventListener('keydown', onKeyEvent);
    };
  }, [hasPermissions, view, searchTerm]);

  useEffect(() => {
    if (Object.keys(filters).length === 0) {
      setTextFilter('');
    }
  }, [filters]);

  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      setView('filter');
      setTextFilter(searchTerm);
    } else {
      setView('no-filter');
    }
  }, [searchTerm]);

  const onApply = (newFilter: string) => {
    dispatch(applyFilter({ type: 'search', selections: newFilter }));
    setTextFilter('');
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!!textFilter && textFilter.trim().length > 0) {
        onApply(textFilter);
        setView('filter');
      } else {
        onApply('');
        setView('no-filter');
      }
    } else if (event.key === 'Escape') {
      setTextFilter(stashedTextFilter.current);
      if (!stashedTextFilter.current || stashedTextFilter.current.trim() === '') {
        setView('no-filter');
      } else {
        setView('filter');
      }
    }
  };

  const onClear = () => {
    setTextFilter('');
    setView('no-filter');
    onApply('');
  };

  const handleEndAdornmentClick = (e: any) => {
    e.preventDefault();
    if (textFilter.length > 0) {
      setTextFilter('');
      setTimeout(() => {
        if (inputRef) inputRef.current?.focus();
      }, 100);
    } else {
      setTextFilter('');
      setView('no-filter');
      onApply('');
    }
  };

  const getView = () => {
    switch (view) {
      case 'filter':
        return (
          <FilterChip
            data-testid="search-filter-value"
            label={
              <>
                Search: <strong style={{ marginLeft: 5 }}>&quot;{searchTerm}&quot;</strong>
              </>
            }
            isSelected
            onClear={onClear}
            onClick={() => {
              stashedTextFilter.current = searchTerm;
              setTextFilter(searchTerm);
              setView('edit');
            }}
          />
        );
      case 'edit':
        return (
          <Grow in timeout={400} style={{ transformOrigin: 'center left' }}>
            <FormControl variant="standard">
              <StyledFilledInput
                inputRef={inputRef}
                sx={{ height: 37, minWidth: 150 }}
                autoFocus
                id="search-bar"
                value={textFilter}
                onKeyUp={handleKeyUp}
                inputProps={{
                  'data-testid': 'text-filter-input',
                  placeholder: "Type '/' to search"
                }}
                onBlur={() => {
                  if (searchTerm && searchTerm.length === 0 && stashedTextFilter.current && stashedTextFilter.current.length === 0) {
                    setTextFilter('');
                    setView('no-filter');
                  } else if (searchTerm && searchTerm.length > 0) {
                    setTextFilter(searchTerm);
                    setView('filter');
                  } else if (stashedTextFilter.current && stashedTextFilter.current.length > 0) {
                    setTextFilter(stashedTextFilter.current);
                    setView('filter');
                  } else {
                    setTextFilter('');
                    setView('no-filter');
                  }
                }}
                onChange={(event) => setTextFilter(event.target.value)}
                endAdornment={
                  <EndAdornmentIcon
                    type={textFilter && textFilter.length ? 'close' : 'search'}
                    icon={textFilter && textFilter.length ? 'close_filled' : 'search'}
                    onMouseDown={handleEndAdornmentClick}
                  />
                }
              />
            </FormControl>
          </Grow>
        );
      case 'no-filter':
      default:
        return (
          <Tooltip title="Search">
            <IconButton
              data-testid="toggle-search"
              onClick={() => {
                setView('edit');
              }}
              aria-label="toggle search field"
            >
              <PolarisIcon icon="search" />
            </IconButton>
          </Tooltip>
        );
    }
  };

  return hasPermissions ? <FilterIconButton isV2={isV2}>{getView()}</FilterIconButton> : <></>;
}

export default SearchBar;

const FilterIconButton = styled.div<{ isV2: boolean }>`
  min-width: ${({ isV2 }) => (isV2 ? 'auto' : '225px')};
`;

const EndAdornmentIcon = styled(PolarisIcon)<{ type: 'search' | 'close' }>`
  cursor: pointer;
  color: ${({ theme, type }) => (type === 'search' ? theme.palette.primary.main : 'inherit')};
  transition: ease opacity 200ms;
  width: 25px;
  height: 25px;
  opacity: ${({ type }) => (type === 'close' ? 0.3 : 0.7)};

  &:hover {
    opacity: ${({ type }) => (type === 'close' ? 0.5 : 1)};
    transition: ease opacity 200ms;
  }
`;

const StyledFilledInput = styled(FilledInput)`
  border: 0;
  border-radius: 50px;
  transition: ease opacity 2000ms;

  input {
    padding: ${({ theme }) => theme.spacing(1.25, 2)};

    &:-webkit-autofill {
      transition: background-color 50000s ease-in-out 0s !important;
      -webkit-box-shadow: 0 0 0 0px transparent inset !important;
    }
  }

  &::placeholder {
    opacity: 0.7;
  }
  &:after,
  &:before {
    display: none;
    border: 0;
  }
`;
